/**
 * IdentityBadge - Lightweight inline identity display
 * 
 * SHARED COMPONENT - Used by all plugins for compact identity display
 * 
 * Use this when:
 * - You need minimal footprint (audit logs, breadcrumbs, inline mentions)
 * - You don't need hover preview
 * - You want fast render (no hover state management)
 * 
 * Usage:
 *   const { IdentityBadge } = (globalThis as any).MembershipComponents || {};
 *   <IdentityBadge arn="auth:account:empowernow:test" name="Test User" type="account" />
 * 
 * @see experience/plugins/people-finder/IDENTITY_CARD_USAGE.md
 */

import * as React from 'react';

export type IdentityType = 'person' | 'group' | 'account' | 'agent' | 'service';

export interface IdentityBadgeProps {
  /** Canonical ARN */
  arn: string;
  /** Display name */
  name: string;
  /** Identity type */
  type: IdentityType;
  /** Show type icon (default: true) */
  showIcon?: boolean;
  /** Show status dot (default: false for compact display) */
  showStatus?: boolean;
  /** Status for color */
  status?: 'active' | 'inactive' | 'orphaned' | 'pending';
  /** Provider name for accounts */
  provider?: string;
  /** Custom click handler */
  onClick?: () => void;
  /** Size variant */
  size?: 'small' | 'medium';
}

const TYPE_ICONS: Record<IdentityType, string> = {
  person: '👤',
  group: '👥',
  account: '🔐',
  agent: '🤖',
  service: '⚙️',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'var(--success-color, #10b981)',
  inactive: 'var(--main-text-secondary, #6b7280)',
  orphaned: 'var(--warning-color, #f59e0b)',
  pending: 'var(--info-color, #3b82f6)',
};

export function IdentityBadge({
  arn,
  name,
  type,
  showIcon = true,
  showStatus = false,
  status = 'active',
  provider,
  onClick,
  size = 'medium',
}: IdentityBadgeProps) {
  const isSmall = size === 'small';
  
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      // Default navigation
      const encoded = encodeURIComponent(arn);
      const routes: Record<IdentityType, string> = {
        person: `/identity-core/people/${encoded}`,
        group: `/identity-core/groups/${encoded}`,
        account: `/identity-core/accounts/${encoded}`,
        agent: `/agents/${encoded}`,
        service: `/identity-core/services/${encoded}`,
      };
      window.location.hash = routes[type];
    }
  };

  const badgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: isSmall ? '4px' : '6px',
    padding: isSmall ? '2px 6px' : '3px 8px',
    background: 'var(--card-bg, rgba(255,255,255,0.05))',
    border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
    borderRadius: '4px',
    fontSize: isSmall ? '11px' : '12px',
    color: 'var(--main-text)',
    cursor: onClick ? 'pointer' : 'default',
    textDecoration: 'none',
    transition: 'all 0.15s ease',
    fontWeight: 500,
  };

  return React.createElement('span', {
    style: badgeStyle,
    onClick: onClick ? handleClick : undefined,
    title: arn,
    role: onClick ? 'button' : undefined,
    tabIndex: onClick ? 0 : undefined,
    'aria-label': `${name} (${type})`,
  },
    showIcon && React.createElement('span', {
      style: { fontSize: isSmall ? '10px' : '12px' },
      'aria-hidden': 'true',
    }, TYPE_ICONS[type]),
    React.createElement('span', {
      style: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '120px',
      }
    }, name),
    showStatus && React.createElement('span', {
      style: {
        width: '5px',
        height: '5px',
        borderRadius: '50%',
        background: STATUS_COLORS[status] || STATUS_COLORS.active,
        flexShrink: 0,
      },
      'aria-label': status,
    })
  );
}

export default IdentityBadge;

